// src/core/lib/widget-params.ts
import type { Option } from '@/components/ui/MultipleSelector';

/**
 * A product_list widget stores its selection in `params` as:
 *
 *   params: { selected_product_details: [ {value, label, productData}, ... ],
 *             product_ids: [919, 5516] }
 *
 * `selected_product_details` is what the selector renders (it carries the
 * product titles); `product_ids` is the same selection reduced to ids, so the
 * server never has to dig through the details to know what was picked. Both are
 * written from one source, so they can't disagree.
 *
 * Older pages are still readable: `params` may be a bare array of options, a
 * bare array of ids, or `{ product_ids: [...] }`.
 */
export interface WidgetProductParams {
  selected_product_details: Option[];
  product_ids: number[];
}

/** Options to show in the selector, whatever shape `params` was stored in. */
export function getSelectedProducts(params: any): Option[] {
  const list = Array.isArray(params)
    ? params
    : Array.isArray(params?.selected_product_details)
      ? params.selected_product_details
      : Array.isArray(params?.product_ids)
        ? params.product_ids
        : [];

  return list.map((item: Option | number | string) =>
    typeof item === 'object' && item !== null
      ? { ...item, value: String(item.value) }
      : // only an id was stored, so there is no title to show
        { value: String(item), label: String(item) }
  );
}

/** `params` in storage shape, with ids derived from the same selection. */
export function buildProductParams(params: any): WidgetProductParams {
  const selected = getSelectedProducts(params);

  return {
    selected_product_details: selected,
    product_ids: selected.map(option => Number(option.value)),
  };
}

/**
 * "See all" destination for a product_list widget: the link picked in the admin
 * (LinkSelector), carrying the widget's own filter so the target page shows the
 * same products as the widget. `is_available=1` is the param the availability
 * filter on the category page already uses.
 */
export function getSeeAllHref(widget: any): string | null {
  const link = widget?.link;
  if (!link) return null;

  if (widget?.type_list === 'new' && widget?.params?.is_available) {
    return `${link}${link.includes('?') ? '&' : '?'}is_available=1`;
  }

  return link;
}

/** `params` for a "newest products" list: the filters applied to the list. */
export interface WidgetNewestParams {
  is_available: boolean;
  selected_category_details: Option[];
  categories: number[];
}

/**
 * Categories to show in the selector. Same idea as getSelectedProducts: the
 * details carry the titles for the chips, `categories` is the id-only form.
 */
export function getSelectedCategories(params: any): Option[] {
  const list = Array.isArray(params?.selected_category_details)
    ? params.selected_category_details
    : Array.isArray(params?.categories)
      ? params.categories
      : [];

  return list.map((item: Option | number | string) =>
    typeof item === 'object' && item !== null
      ? { ...item, value: String(item.value) }
      : { value: String(item), label: String(item) }
  );
}

/**
 * Put every product_list widget's `params` into storage shape, which depends on
 * how the list is built:
 *
 *   type_list "select" -> { selected_product_details, product_ids }
 *   type_list "new"    -> { is_available }
 *
 * Other widgets (text, image, banner, ...) are passed through untouched.
 */
export function withProductParams<T>(content: T): T {
  if (!Array.isArray(content)) return content;

  return content.map((widget: any) => {
    const listType = widget?.type_list;
    if (listType !== 'new' && listType !== 'select' && listType !== 'inventory') {
      return widget;
    }

    // pages saved while the ids also sat next to `params` still carry that
    // top-level copy; drop it, `params.product_ids` is the only one kept
    const { product_ids: _legacyTopLevelIds, ...rest } = widget;

    // the backend reads no params for this list yet (categories come later),
    // so don't carry over a selection left from another mode
    if (listType === 'inventory') {
      return { ...rest, params: {} };
    }

    if (widget.type_list === 'select') {
      return { ...rest, params: buildProductParams(widget.params) };
    }

    const categories = getSelectedCategories(widget.params);

    return {
      ...rest,
      params: {
        is_available: !!widget?.params?.is_available,
        selected_category_details: categories,
        categories: categories.map(option => Number(option.value)),
      },
    };
  }) as T;
}
